import { CheckedEnvironment } from "./types";

export type TenantStatus = "online" | "offline" | "mixed";

export const ENVIRONMENT_HEALTH_FILTERS = [
  "all",
  "issues",
  "mixed",
  "offline",
  "online",
] as const;

export type EnvironmentHealthFilter =
  (typeof ENVIRONMENT_HEALTH_FILTERS)[number];

export function sortOnName(a: CheckedEnvironment, b: CheckedEnvironment) {
  if (a.organisation < b.organisation) {
    return -1;
  }
  if (a.organisation > b.organisation) {
    return 1;
  }
  return 0;
}

export function deriveTenantStatus(
  environment: CheckedEnvironment,
): TenantStatus {
  if (environment.status === "online" && environment.cmsStatus === "online") {
    return "online";
  }
  if (environment.status === "offline" && environment.cmsStatus === "offline") {
    return "offline";
  }
  return "mixed";
}

export function environmentMatchesQuery(
  environment: CheckedEnvironment,
  query: string,
): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) {
    return true;
  }
  if (environment.organisation.toLowerCase().includes(needle)) {
    return true;
  }
  return Boolean(environment.version?.toLowerCase().includes(needle));
}

/** Sentinel group key for missing / unparseable API versions. */
export const UNKNOWN_API_VERSION = "unknown";

/**
 * Parse a dotted numeric version (optional leading `v`).
 * Returns null for missing, empty, or non-numeric strings.
 */
export function parseApiVersion(version: string | undefined): number[] | null {
  if (!version) {
    return null;
  }
  const trimmed = version.trim();
  if (!trimmed) {
    return null;
  }
  const withoutV = trimmed.replace(/^v/i, "");
  const parts = withoutV.split(".");
  if (parts.length === 0 || parts.some((part) => part === "")) {
    return null;
  }
  const numbers: number[] = [];
  for (const part of parts) {
    if (!/^\d+$/.test(part)) {
      return null;
    }
    numbers.push(Number(part));
  }
  return numbers;
}

/** Compare two parseable version strings. Positive if a > b. */
export function compareApiVersions(a: string, b: string): number {
  const left = parseApiVersion(a);
  const right = parseApiVersion(b);
  if (!left && !right) {
    return 0;
  }
  if (!left) {
    return -1;
  }
  if (!right) {
    return 1;
  }
  const len = Math.max(left.length, right.length);
  for (let i = 0; i < len; i += 1) {
    const l = left[i] ?? 0;
    const r = right[i] ?? 0;
    if (l !== r) {
      return l - r;
    }
  }
  return 0;
}

/** Canonical group key, or `unknown` when the version is missing/unparseable. */
export function environmentApiVersionKey(
  environment: CheckedEnvironment,
): string {
  const parsed = parseApiVersion(environment.version);
  if (!parsed) {
    return UNKNOWN_API_VERSION;
  }
  return parsed.join(".");
}

/** Highest parseable API version across environments, or null if none. */
export function latestApiVersion(
  environments: CheckedEnvironment[],
): string | null {
  let latest: string | null = null;
  for (const environment of environments) {
    const key = environmentApiVersionKey(environment);
    if (key === UNKNOWN_API_VERSION) {
      continue;
    }
    if (latest === null || compareApiVersions(key, latest) > 0) {
      latest = key;
    }
  }
  return latest;
}

export type ApiVersionGroup = {
  version: string;
  environments: CheckedEnvironment[];
  behind: boolean;
};

/**
 * Group environments by API version: newest first, unknown last.
 * Within each group, environments stay sorted by organisation name.
 */
export function groupEnvironmentsByApiVersion(
  environments: CheckedEnvironment[],
  latest: string | null,
): ApiVersionGroup[] {
  const buckets = new Map<string, CheckedEnvironment[]>();

  for (const environment of environments) {
    const key = environmentApiVersionKey(environment);
    const list = buckets.get(key);
    if (list) {
      list.push(environment);
    } else {
      buckets.set(key, [environment]);
    }
  }

  const keys = Array.from(buckets.keys()).sort((a, b) => {
    if (a === UNKNOWN_API_VERSION) {
      return 1;
    }
    if (b === UNKNOWN_API_VERSION) {
      return -1;
    }
    return compareApiVersions(b, a);
  });

  return keys.map((version) => {
    const groupEnvs = (buckets.get(version) ?? []).slice().sort(sortOnName);
    const behind =
      version !== UNKNOWN_API_VERSION &&
      latest !== null &&
      compareApiVersions(version, latest) < 0;
    return { version, environments: groupEnvs, behind };
  });
}

/** Whether the UI should skip version section headers (quiet steady state). */
export function shouldShowQuietVersionSummary(
  groups: ApiVersionGroup[],
  latest: string | null,
): boolean {
  if (groups.length === 0) {
    return true;
  }
  if (groups.length > 1) {
    return false;
  }
  const only = groups[0];
  if (only.version === UNKNOWN_API_VERSION) {
    return true;
  }
  return latest !== null && only.version === latest && !only.behind;
}

export function environmentMatchesHealth(
  environment: CheckedEnvironment,
  health: EnvironmentHealthFilter,
): boolean {
  if (health === "all") {
    return true;
  }
  const status = deriveTenantStatus(environment);
  if (health === "issues") {
    return status !== "online";
  }
  return status === health;
}

export function countEnvironmentsByHealth(
  environments: CheckedEnvironment[],
): Record<EnvironmentHealthFilter, number> {
  const counts: Record<EnvironmentHealthFilter, number> = {
    all: environments.length,
    issues: 0,
    mixed: 0,
    offline: 0,
    online: 0,
  };

  for (const environment of environments) {
    const status = deriveTenantStatus(environment);
    counts[status] += 1;
    if (status !== "online") {
      counts.issues += 1;
    }
  }

  return counts;
}
